import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  StakeCurrency,
  TournamentEntryAllOfStakeOptionId,
} from "@nestcoinco/hyper-api-gateway-api-client";

import { handleEnterDuel } from "../../api-clients/tournamentsClient";
import { QUERY_KEYS } from "../constants";
import { useAtom, useSetAtom } from "jotai";
import { duelEntryAtomWithStorage } from "../../jotai/atoms/tournament";
import { GameFlowState, GameType, ROUTES } from "../../utils/constants";
import {
  didUserClickToInitiateGameAtom,
  gameFlowStateAtomWithStorage,
  gameTypeAtom,
} from "../../jotai/atoms/games";
import useAnalytics from "../../hooks/useAnalytics";

function useDuelEntryMutation(redirectPath?: string) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [, setDuelEntry] = useAtom(duelEntryAtomWithStorage);
  const [, setGameFlowState] = useAtom(gameFlowStateAtomWithStorage);
  const setGameType = useSetAtom(gameTypeAtom);
  const setDidUserClickToInitiateGame = useSetAtom(
    didUserClickToInitiateGameAtom
  );

  const mutation = useMutation({
    mutationKey: [QUERY_KEYS.TOURNAMENT.DUEL_ENTRY],
    mutationFn: handleEnterDuel,
    onSuccess: (data) => {
      const { id, game, stakeAmount } = data.tournament;
      analytics.sendInitiateGameEvent({
        entry_fee: stakeAmount,
        game_name: game.name,
        game_mode: GameType.BATTLE,
      });
      setDuelEntry(data);
      setGameType(GameType.BATTLE);
      const path = redirectPath || `${ROUTES.GAMES.ROOT}/${game.slug}/${id}`;
      setGameFlowState(GameFlowState.STARTING_GAME);
      navigate(path);
    },
  });

  const playBattleGame = useCallback(
    (
      stakeOptionId: TournamentEntryAllOfStakeOptionId,
      gameId: string,
      currency = StakeCurrency.HPR
    ) => {
      setDidUserClickToInitiateGame(true);
      mutation.mutate({
        gameId,
        stakeOptionId,
        currency,
      });
    },
    [mutation, setDidUserClickToInitiateGame]
  );

  return { mutation, playBattleGame };
}

export default useDuelEntryMutation;
